import { Component, computed, DestroyRef, OnInit, output } from '@angular/core';
import { features } from '@consts/text.const';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { UniformsSelectors } from '@states/uniforms/uniforms.selector-types';
import { UniformsActions } from '@states/uniforms/uniforms.action-types';
import { UniformModels } from '@models/uniform.model';
import { UiUniformUpdateNameDialogService } from '../../../../../shared/ui-kit/ui-uniform-update-name-dialog/ui-uniform-update-name-dialog.service';
import { ConfirmDialogService } from '../../../../../shared/confirm-dialog/confirm-dialog.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmDialogModel, UiNoDataComponent } from '@ui';
import { UiKitModule } from '../../../../../shared/ui-kit/ui-kit.module';
import { syncQueryParam } from '../../../../../helpers/sync-query-param.helper';
import { UiSearchComponent } from '../../../../../shared/ui-kit/ui-search/ui-search.component';
import { UiUniformCardComponent } from '../../../../../shared/ui-kit/ui-uniform-card/ui-uniform-card.component';
import { UiPreloaderComponent } from '../../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type ViewState = 'LOADING' | 'HAS_DATA' | 'NO_RESULTS' | 'INITIAL_EMPTY';

@Component({
  selector: 'app-existing-uniforms',
  templateUrl: './existing-uniforms.component.html',
  styleUrls: ['./existing-uniforms.component.scss'],
  imports: [
    UiKitModule,
    TranslatePipe,
    UiSearchComponent,
    UiNoDataComponent,
    UiUniformCardComponent,
    UiPreloaderComponent,
  ],
})
export class ExistingUniformsComponent implements OnInit {
  public navigateToUnsaved = output<void>();
  public selectIsFirstLoad = this.store$.selectSignal(UniformsSelectors.selectIsFirstLoad);

  protected query = syncQueryParam<string>('search');

  protected filteredUniforms = computed(() => {
    const query = this.query();
    const savedUniforms = this.selectSavedUniforms();

    if (!query) {
      return savedUniforms;
    }

    return savedUniforms.filter((item) => item.name?.toLowerCase().includes(query.toLowerCase()));
  });

  protected readonly noResults = features.uniform.noResults;
  protected readonly noData = features.uniform.noData;

  private selectSavedUniforms = this.store$.selectSignal(UniformsSelectors.selectSavedUniforms);
  private isLoading = this.store$.selectSignal(UniformsSelectors.selectIsLoading);

  public readonly vm$ = computed(() => {
    const isLoading = this.isLoading();
    const savedUniforms = this.selectSavedUniforms();
    const filteredUniforms = this.filteredUniforms();
    const query = this.query();

    let viewState: ViewState;
    if (isLoading && !savedUniforms.length) {
      viewState = 'LOADING';
    } else if (savedUniforms.length > 0 && query && !filteredUniforms.length) {
      viewState = 'NO_RESULTS';
    } else if (savedUniforms.length > 0) {
      viewState = 'HAS_DATA';
    } else {
      viewState = 'INITIAL_EMPTY';
    }

    return {
      viewState,
      filteredUniforms,
      query,
    };
  });

  constructor(
    private store$: Store,
    private actions$: Actions,
    private uniformUpdateNameDialogService: UiUniformUpdateNameDialogService,
    private confirmDialogService: ConfirmDialogService,
    private translateService: TranslateService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.store$.dispatch(UniformsActions.getSavedUniforms());

    // Listen for successful delete operations to refresh the list
    this.actions$.pipe(ofType(UniformsActions.deleteUniformSuccess), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // Refresh the saved uniforms list after successful deletion
      this.store$.dispatch(UniformsActions.getSavedUniforms());
    });
  }

  public editUniform(uniform: UniformModels.Uniform) {
    this.uniformUpdateNameDialogService
      .open({
        currentName: uniform.name || '',
        uniformId: String(uniform.uniformId || ''),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.newName && uniform.uniformId) {
          // Dispatch the update uniform name action
          this.store$.dispatch(
            UniformsActions.updateUniformName({
              uniformId: uniform.uniformId,
              name: result.newName,
            }),
          );
        }
      });
  }

  public removeUniform(uniform: UniformModels.Uniform) {
    // Show confirmation dialog before deleting
    this.confirmDialogService
      .open({
        title: this.translateService.instant('UNIFORM.DELETE_CONFIRM_TITLE', {
          name: uniform.name || 'this uniform',
        }),
        msg: this.translateService.instant('UNIFORM.DELETE_CONFIRM_MESSAGE', {
          name: uniform.name || 'this uniform',
        }),
        confirm: this.translateService.instant('COMMON.DELETE'),
        cancel: this.translateService.instant('COMMON.CANCEL'),
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        disableClose: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.selection) {
          // User confirmed deletion
          if (uniform.uniformId) {
            this.store$.dispatch(UniformsActions.deleteUniform({ uniformId: uniform.uniformId }));
          }
        }
      });
  }
}
